import type { BadgeProps } from '@magicschool/spellbook/components/Badge';
import { Badge } from '@magicschool/spellbook/components/Badge';
import { Breadcrumb, type BreadcrumbProps } from '@magicschool/spellbook/components/Breadcrumb';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { ComponentProps } from 'react';
import type { BreadcrumbData } from './PageHeader';

/** Renders breadcrumb entries as `Breadcrumb` crumbs; shared by the PageHeader and PageContainer breadcrumb slots. */
export function renderBreadcrumbItems(breadcrumbs: readonly BreadcrumbData[]) {
  // Breadcrumb renders each crumb in a list item keyed by its position, so a crumb's own key adds nothing.
  return breadcrumbs.map(({ element: Comp, label, href, type, onClick }, index) =>
    type === 'link' ? (
      <Breadcrumb.Link key={index} asChild={Boolean(Comp)} href={href} onClick={onClick}>
        {Comp ? <Comp href={href}>{label}</Comp> : label}
      </Breadcrumb.Link>
    ) : (
      <Breadcrumb.Page key={index} onClick={onClick}>
        {label}
      </Breadcrumb.Page>
    ),
  );
}

export const PageHeaderBreadcrumbSlot = Slot<BreadcrumbProps & { breadcrumbs: BreadcrumbData[] }>({
  displayName: 'PageHeader.Breadcrumb',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: ({ breadcrumbs, ...props }) => {
    return breadcrumbs?.length ? (
      <Breadcrumb data-spellbook-name="page-header-breadcrumb" {...props}>
        {renderBreadcrumbItems(breadcrumbs)}
      </Breadcrumb>
    ) : null;
  },
});

export const PageHeaderTitleSlot = Slot<TextProps>({
  displayName: 'PageHeader.Title',
  renderDefault: ({ ...props }) => (
    <Text data-spellbook-name="page-header-title" element="h1" variant="heading-2" tabIndex={-1} {...props} />
  ),
});

export const PageHeaderTitleBadgeSlot = Slot<BadgeProps>({
  displayName: 'PageHeader.TitleBadge',
  renderDefault: (props) => <Badge data-spellbook-name="page-header-title-badge" size="sm" variant="primary" {...props} />,
});

export const PageHeaderDescriptionSlot = Slot<TextProps>({
  displayName: 'PageHeader.Description',
  renderDefault: (props) => <Text data-spellbook-name="page-header-description" variant="body-2" {...props} />,
});

export const PageHeaderActionsSlot = Slot<ComponentProps<'div'>>({
  displayName: 'PageHeader.Actions',
  renderDefault: ({ className, ...props }) => {
    return <div data-spellbook-name="page-header-actions" className={cn('flex flex-wrap items-center gap-2 mb-3', className)} {...props} />;
  },
});

export const PageHeaderAlertSlot = Slot<ComponentProps<'div'>>({
  displayName: 'PageHeader.Alert',
  renderDefault: ({ className, ...props }) => {
    return <div data-spellbook-name="page-header-alert" className={cn('w-full', className)} {...props} />;
  },
});
